import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  BackofficeUnreadableError,
  type ContractDetail,
  type ContractReconciliation,
  type ContractSummary,
} from '@/types/client-contract';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** Consumption moves when the cost figures refresh, every half hour. */
export const CLIENT_CONTRACTS_POLL_MS = 60_000;

export interface ClientContractsQuery {
  teamId?: string;
  /** Only contracts covering this ISO date. */
  activeOn?: string;
}

/**
 * Every matching contract with its drawdown summary, polled with ETag
 * revalidation. The query is a `refetchDeps` entry, so a filter change reads
 * again in full rather than revalidating against the old filter's token.
 */
export function useClientContracts(query: ClientContractsQuery = {}) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const poll = usePoll<ContractSummary[]>({
    interval: CLIENT_CONTRACTS_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listClientContracts(queryRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  return {
    contracts: poll.data ?? [],
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
  };
}

/** One contract with every line in its window. Read once per contract, and
 * again on `refetch` — after a refresh of the cost figures, say. */
export function useClientContract(contractId: string | undefined) {
  const poll = usePoll<ContractDetail>({
    interval: null,
    enabled: Boolean(contractId),
    refetchDeps: [contractId],
    queryFn: ({ signal }) => vnextApiClient.getClientContract(contractId as string, { signal }),
    onError: (err) => Sentry.captureException(toError(err)),
  });

  return {
    contract: poll.data ?? null,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
  };
}

export interface ContractReconciliationState {
  reconciliation: ContractReconciliation | null;
  isRunning: boolean;
  /** Backoffice could not be read: the comparison is unavailable, the Hub's
   * own figures are not in doubt. */
  backofficeUnreadable: string | null;
  error: Error | null;
  run: () => Promise<void>;
}

/**
 * The contract's comparison with Backoffice, run on request: it reads
 * Backoffice live, which opening the page should not cost. Cleared when the
 * contract changes.
 */
export function useContractReconciliation(contractId: string | undefined) {
  const [reconciliation, setReconciliation] = useState<ContractReconciliation | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [backofficeUnreadable, setBackofficeUnreadable] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const currentId = useRef(contractId);

  useEffect(() => {
    currentId.current = contractId;
    setReconciliation(null);
    setBackofficeUnreadable(null);
    setError(null);
  }, [contractId]);

  const run = useCallback(async () => {
    if (!contractId) {
      return;
    }
    setIsRunning(true);
    setBackofficeUnreadable(null);
    setError(null);
    try {
      const result = await vnextApiClient.getContractReconciliation(contractId);
      if (currentId.current === contractId) {
        setReconciliation(result);
      }
    } catch (err) {
      if (currentId.current !== contractId) {
        return;
      }
      if (err instanceof BackofficeUnreadableError) {
        // Expected while Backoffice is down or lacks the endpoint: not a bug.
        setBackofficeUnreadable(err.message);
      } else {
        const e = toError(err);
        Sentry.captureException(e);
        setError(e);
      }
    } finally {
      if (currentId.current === contractId) {
        setIsRunning(false);
      }
    }
  }, [contractId]);

  return { reconciliation, isRunning, backofficeUnreadable, error, run };
}
